import React from 'react';
import { motion } from 'framer-motion';
import { Download, ArrowRight, CheckCircle, Building2, MapPin, Users, Wifi, Car, Calculator as Elevator, Star, Phone, Globe } from 'lucide-react';

const AlBazCenter: React.FC<{ content?: any }> = ({ content }) => {
  const advantages = content?.advantages || [
    "Distinctive location at Salalah commercial district.",
    "A glass building characterized with the art of modern architecture.",
    "Immediately overlooking 23rd July St. and Sultan Qaboos Mosque.",
    "A Building specially designated for the service of companies and businessmen.",
    "Availability of various spacious car parks on four directions.",
    "Equipped with two elevators with speed of 1.75 m per second."
  ];


  const services = content?.services || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => content?.[`center_service_${i}`]).filter(Boolean);
  const displayServices = services.length > 0 ? services : [
    "Lease of fully furnished offices of different areas",
    "Reception and communication services",
    "Distinct address placement on entrance",
    "Modern office furniture and decorations",
    "Executive secretariat management",
    "Internet service (ADSL, WIFI)",
    "Meeting room with visual aids",
    "Printing, photocopying, binding services",
    "Tea and Coffee delivery service",
    "Public relations and government liaison services"
  ];

  const floorPlans = [
    {
      title: content?.center_plan_1_title || "Floors 1, 2, 8",
      description: content?.center_plan_1_desc || "Offices with a variety of areas inclusive of all services (toilets and kitchen), which are managed by AL Baz Real Estate Development. They are open-floor design to provide wide areas for companies that seek independence in delivering their services within an area of 467 square meter for each floor.",
      image: content?.center_plan_1_image || "/Al Baz Center/Floors (1 , 2 , 8).jpg"
    },
    {
      title: content?.center_plan_2_title || "Floors 3, 4",
      description: content?.center_plan_2_desc || "Offices with a variety of areas inclusive of all services (toilets and kitchen) and they are fully furnished. These floors are divided into two models and managed by AL Baz Real Estate Development. They are of a distinct design to provide areas for the medium companies that seek independence in delivering their services. The First Model: Offices and services on an area of (247 square meter).",
      image: content?.center_plan_2_image || "/Al Baz Center/Floors (3 , 4).jpg"
    }
  ];

  const interiorImages = [
    content?.center_interior_1 || "/Al Baz Center/civilized_1.jpg",
    content?.center_interior_2 || "/Al Baz Center/civilized_2.jpg",
    content?.center_interior_3 || "/Al Baz Center/civilized_3.jpg",
    content?.center_interior_4 || "/Al Baz Center/civilized_4.jpg",
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg border">


        {/* Hero Section */}
        <motion.section
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative bg-white overflow-hidden mb-0"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-4">
            {/* Left Content */}
            <div className="p-12  flex flex-col justify-center">
              <motion.h1
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-6"
              >
                {content?.title || "Al Baz Business Center"}
              </motion.h1>

              <div className="text-gray-700 text-sm md:text-base text-justify leading-relaxed mb-8" dangerouslySetInnerHTML={{ __html: (content?.desc || "The center is designated for delivering distinctive business services encompassing all requirements of companies and businessmen under one ceiling. The center is characterized by its spectacular location on 23rd’July Street, which is known for its service and commercial fundamentals, in addition to its distinguished location in the middle of the commercial area and its vicinity of Salalah Airport, which makes it the best location that can attract those who seek distinctive services; and thus it provides assurance and convenient working environment.").replace(/\n/g, '<br/>') }} />

              <h2 className="text-2xl font-bold text-gray-900 mb-6">Key Advantages</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-4">
                {advantages.map((adv: any, index: number) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="flex items-start p-4 bg-white border rounded-lg shadow-sm hover:shadow-md transition"
                  >
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold mr-3">
                      {index + 1}
                    </span>
                    <span className="text-gray-700 text-sm">{adv}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Right Image (smaller & closer) */}
            <div className="flex justify-center items-center p-4">
              <img
                src={content?.image || "/Al-Baz-Business-Center.jpg"}
                alt="Al Baz Business Center"
                className="w-full h-full object-cover rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </motion.section>


        {/* Sophisticated Services Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="rounded-3xl p-12 mt-0 mb-10"
        >
          <div className="text-center mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
              Sophisticated Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
            {displayServices.map((service: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl"
              >
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-gray-700 font-medium">{service}</span>
              </motion.div>
            ))}
          </div>

          <div className="text-center mb-12 border-t pt-10">
            <h2 className="text-1xl md:text-2xl font-bold text-gray-900 mb-6">
              {content?.objective || "Our objective is to deliver distinctive Services through sophisticated civilized means"}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {interiorImages.map((image: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.15, ease: "easeOut" }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300"
              >
                <motion.img
                  src={image}
                  alt={`Interior Space ${index + 1}`}
                  initial={{ scale: 1.1 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 left-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <p className="font-semibold">Interior Space {index + 1}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Floor Plans Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
              Floor Plans & Layouts
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 place-items-center">
            {floorPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="bg-white max-w-md w-full rounded-2xl shadow-lg overflow-hidden 
                   hover:shadow-2xl transition-all duration-300"
              >
                {/* Image Box (smaller height but full image inside) */}
                <div className="relative flex justify-center items-center bg-white-50 h-48">
                  <motion.img
                    src={plan.image}
                    alt={plan.title}
                    initial={{ scale: 1.02 }}
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.5 }}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>

                {/* Content */}
                <div className="p-5">
                  <h3 className="text-lg font-bold text-center text-gray-900 mb-2">{plan.title}</h3>
                  <p className="text-gray-600 text-sm text-justify leading-relaxed">{plan.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default AlBazCenter;